import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Link,
  Divider,
  Chip,
  Grid,
  Pagination,
  Card,
  CardMedia,
  CardContent,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LinkIcon from "@mui/icons-material/Link";
import { getNoticiasEventos } from "../../services/noticiasEventosService";

const ITEMS_PER_PAGE = 9;

const getNoticiaEventoId = (item) =>
  item?.id ?? item?.Id ?? item?.idNoticiaEvento ?? item?.IdNoticiaEvento ?? item?.idNoticiasEventos ?? item?.idnoticiaseventos;

const DEFAULT_IMAGE =
  "https://images.unsplash.com/photo-1557683316-973673baf926?w=800&h=600&fit=crop";

const ensureUrl = (value) => {
  if (!value || typeof value !== "string") return "";
  const v = value.trim();
  return /^https?:\/\//i.test(v) ? v : v ? `https://${v}` : "";
};

const MOCK_ITEMS = [
  {
    id: 1,
    Titulo: "ENERGY CONSERVATION",
    Resumen: "Cum sociis natoque penatibus et magnis dis parturient montes, nascetur ridiculus mus. Nulla dui. Fusce feugiat malesuada odio.",
    Contenido: "Contenido completo de la noticia o evento.",
    ImagenUrl: "",
    Tipo: "Noticia",
    CategoriaId: 0,
    FechaPublicacion: "2024-01-15",
    EnlaceEvento: "",
    Estado: "Publicado",
    EsPublico: 1,
  },
  {
    id: 2,
    Titulo: "VIDEO INSIDE THIS POST",
    Resumen: "Cum sociis natoque penatibus et magnis dis parturient montes, nascetur ridiculus mus. Nulla dui. Fusce feugiat malesuada odio.",
    Contenido: "Contenido completo.",
    ImagenUrl: "",
    Tipo: "Evento",
    CategoriaId: 0,
    FechaPublicacion: "2024-01-14",
    EnlaceEvento: "",
    Estado: "Publicado",
    EsPublico: 1,
  },
  {
    id: 3,
    Titulo: "KIDS CAN VOLUNTEER",
    Resumen: "Cum sociis natoque penatibus et magnis dis parturient montes, nascetur ridiculus mus. Nulla dui. Fusce feugiat malesuada odio.",
    Contenido: "Contenido completo.",
    ImagenUrl: "",
    Tipo: "Noticia",
    CategoriaId: 0,
    FechaPublicacion: "2024-01-13",
    EnlaceEvento: "",
    Estado: "Publicado",
    EsPublico: 1,
  },
];

const normalizeNoticiasEventos = (data) => {
  const raw = Array.isArray(data) ? data : data?.data ?? data?.noticiasEventos ?? data?.noticias ?? [];
  const list = Array.isArray(raw) ? raw : [];
  if (list.length === 0) return MOCK_ITEMS;
  return list
    .filter((e) => e.Estado !== "Borrado" && Number(e.EsPublico) === 1)
    .map((e) => ({
      id: getNoticiaEventoId(e),
      Titulo: e.Titulo ?? "",
      Resumen: e.Resumen ?? "",
      Contenido: e.Contenido ?? "",
      ImagenUrl: e.ImagenUrl ?? e.imagenUrl ?? "",
      Tipo: e.Tipo ?? "Noticia",
      CategoriaId: Number(e.CategoriaId) || 0,
      FechaPublicacion: e.FechaPublicacion ?? null,
      FechaEventoInicio: e.FechaEventoInicio ?? null,
      FechaEventoFin: e.FechaEventoFin ?? null,
      Ubicacion: e.Ubicacion ?? "",
      Modalidad: e.Modalidad ?? "",
      EnlaceEvento: e.EnlaceEvento ?? "",
      Estado: e.Estado ?? "Publicado",
      EsPublico: 1,
    }));
};

const NewsEvents = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [modalItem, setModalItem] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    getNoticiasEventos()
      .then((data) => {
        if (mounted) setItems(normalizeNoticiasEventos(data));
      })
      .catch(() => {
        if (mounted) setItems(MOCK_ITEMS);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => { mounted = false; };
  }, []);

  const totalPages = Math.max(1, Math.ceil(items.length / ITEMS_PER_PAGE));
  const start = (page - 1) * ITEMS_PER_PAGE;
  const pageItems = items.slice(start, start + ITEMS_PER_PAGE);

  const openModal = (item) => setModalItem(item);
  const closeModal = () => setModalItem(null);
  const hasLink = (item) => !!ensureUrl(item?.EnlaceEvento);

  const formatFecha = (f) => {
    if (!f) return null;
    try {
      const d = new Date(f);
      return isNaN(d.getTime()) ? null : d.toLocaleDateString("es-CO", { day: "numeric", month: "short", year: "numeric" });
    } catch {
      return String(f).slice(0, 10);
    }
  };

  return (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column", alignItems: "center", backgroundColor: "#fff" }}>
      {/* Hero */}
      <Box
        sx={{
          width: "100%",
          py: { xs: 4, md: 6 },
          px: 2,
          background: (theme) =>
            `linear-gradient(135deg, ${theme.palette.tertiary?.main || "#7c9270"} 0%, ${theme.palette.primary?.main || "#113253"} 100%)`,
          color: "#fff",
          textAlign: "center",
        }}
      >
        <Typography
          variant="h1"
          sx={{
            fontSize: { xs: "1.75rem", sm: "2.25rem", md: "2.75rem" },
            fontWeight: 700,
            letterSpacing: "0.05em",
            textTransform: "uppercase",
            mb: 1,
          }}
        >
          NOTICIAS & EVENTOS
        </Typography>
        <Typography variant="subtitle1" sx={{ opacity: 0.95 }}>
          Subtitulo
        </Typography>
      </Box>

      <Container maxWidth="lg" sx={{ py: 4, width: "100%" }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
        <>
        <Grid container spacing={3}>
          {pageItems.map((item) => (
            <Grid item xs={12} sm={6} md={4} key={item.id}>
              <Card
                onClick={() => openModal(item)}
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  borderRadius: 2,
                  overflow: "hidden",
                  cursor: "pointer",
                  boxShadow: 1,
                  "&:hover": { boxShadow: 3 },
                }}
              >
                <CardMedia
                  component="img"
                  height="200"
                  image={item.ImagenUrl || DEFAULT_IMAGE}
                  alt={item.Titulo}
                  onError={(e) => { e.target.src = DEFAULT_IMAGE; }}
                  sx={{ objectFit: "cover", height: { xs: 190, sm: 200 } }}
                />
                <CardContent sx={{ flex: 1, display: "flex", flexDirection: "column", p: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, lineHeight: 1.3 }}>
                    {item.Titulo}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      mb: 1,
                      display: "block",
                      marginBottom: 0,
                    }}
                  >
                    {item.Tipo.toUpperCase()}
                    {item.CategoriaId ? ` · Categoría ${item.CategoriaId}` : ""}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1.25, lineHeight: 1.6 }}>
                    {(item.Resumen || item.Contenido || "").slice(0, 120)}
                    {(item.Resumen || item.Contenido || "").length > 120 ? "..." : ""}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {totalPages > 1 && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 4, gap: 1, alignItems: "center", flexWrap: "wrap" }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, value) => setPage(value)}
              color="primary"
              showFirstButton
              showLastButton
              siblingCount={1}
              boundaryCount={1}
              sx={{ "& .MuiPaginationItem-root": { fontWeight: 600 } }}
            />
            {page < totalPages && (
              <Typography
                component="button"
                type="button"
                variant="body2"
                onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
                sx={{
                  color: "primary.main",
                  fontWeight: 600,
                  cursor: "pointer",
                  border: "none",
                  background: "none",
                  padding: 0,
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                Siguiente &gt;
              </Typography>
            )}
          </Box>
        )}
        </>
        )}
      </Container>

      {/* Modal detalle */}
      <Dialog
        open={!!modalItem}
        onClose={closeModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: "0 24px 48px rgba(0,0,0,0.12)",
            overflow: "hidden",
            bgcolor: "#fff",
            mx: { xs: 1.5, sm: 3 },
          },
        }}
      >
        {modalItem && (
          <>
            <DialogTitle
              sx={{
                py: 2,
                px: 3,
                bgcolor: "#fff",
                borderBottom: "1px solid",
                borderColor: "divider",
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: "text.primary", lineHeight: 1.3 }}>
                  {modalItem.Titulo}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {modalItem.Tipo}
                  {modalItem.FechaPublicacion && ` · ${formatFecha(modalItem.FechaPublicacion)}`}
                </Typography>
              </Box>
              <IconButton
                aria-label="Cerrar"
                onClick={closeModal}
                size="small"
                sx={{ color: "text.secondary", "&:hover": { bgcolor: "action.hover", color: "text.primary" } }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 0, bgcolor: "#fff" }}>
              <Box
                component="img"
                src={modalItem.ImagenUrl || DEFAULT_IMAGE}
                alt={modalItem.Titulo}
                onError={(e) => { e.target.src = DEFAULT_IMAGE; }}
                sx={{ width: "100%", maxHeight: 280, objectFit: "cover", display: "block" }}
              />
              <Box sx={{ px: 3, py: 2.5 }}>
                <Typography variant="body1" sx={{ whiteSpace: "pre-wrap", lineHeight: 1.7, color: "text.primary" }}>
                  {modalItem.Contenido || modalItem.Resumen}
                </Typography>
                {modalItem.Ubicacion && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    Ubicación: {modalItem.Ubicacion}
                  </Typography>
                )}
                {modalItem.Modalidad && (
                  <Typography variant="body2" color="text.secondary">
                    Modalidad: {modalItem.Modalidad}
                  </Typography>
                )}
              </Box>
              <Divider />
              <Box
                sx={{
                  px: 3,
                  py: 2,
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  gap: 1.5,
                  bgcolor: "#fafafa",
                }}
              >
                {modalItem.FechaPublicacion && (
                  <Chip
                    label={formatFecha(modalItem.FechaPublicacion)}
                    size="small"
                    variant="outlined"
                    sx={{ borderRadius: 1, borderColor: "divider", color: "text.secondary", fontWeight: 500 }}
                  />
                )}
                {hasLink(modalItem) && (
                  <Link
                    href={ensureUrl(modalItem.EnlaceEvento)}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.5,
                      color: "primary.main",
                      fontWeight: 600,
                      fontSize: "0.875rem",
                    }}
                  >
                    <LinkIcon fontSize="small" />
                    Ver enlace
                  </Link>
                )}
              </Box>
            </DialogContent>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default NewsEvents;
